import { Fragment } from "react";
import { styled } from "@mui/material";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { Link, Typography } from "@kaltura/ds-react-components";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";

export interface CustomMetadataLineProps {
    // Field label text
    label: string;
    // Value items, rendered as paragraphs or as a comma-separated string depending on the "paragraphs" prop
    items: CustomMetadataItem[];
    // true - render the items as paragraphs, false - as a comma-separated string
    paragraphs?: boolean;
    analyticsData?: {
        actionName: string;
        buttonType: ButtonClickAnalyticsType;
    };
}

export interface CustomMetadataItem {
    // Text to display
    label: string;
    // Link URL, optional. Skip to render as regular text
    url?: string;
}

/**
 * One line of custom metadata (a label, and one or more values) for the media view page.
 * Also handles "Appears in" and "Tags" lines.
 */
export const CustomMetadataLine = ({ label, items, paragraphs, analyticsData }: CustomMetadataLineProps) => {
    const sendButtonAnalytics = useButtonAnalytics();

    const onLinkClick = () => {
        if (!analyticsData) {
            return;
        }
        const { actionName, buttonType } = analyticsData;
        sendButtonAnalytics(actionName, buttonType);
    };
    const className = "kms-ds-media-page-metadata-custom-metadata-item";
    // CSS class names can't contain spaces
    const labelNoSpaces = label.replace(/\s+/g, "");

    const containerClassName = `${className} ${className}-${labelNoSpaces}`;
    const labelElement = (
        <>
            <StyledLabel variant={"body2"} component={"span"}>
                {label}
            </StyledLabel>
            &nbsp;
        </>
    );
    const typographyClassName = `${className}-container ${className}-${labelNoSpaces}-container`;

    if (paragraphs) {
        return (
            <>
                {items.map(({ label }, index) => (
                    <StyledLine className={containerClassName} key={index}>
                        {index === 0 && labelElement}
                        <StyledTypographyWithHtml className={typographyClassName} variant={"body2Highlight"}>
                            {HtmlReactParser(label)}
                        </StyledTypographyWithHtml>
                    </StyledLine>
                ))}
            </>
        );
    }

    return (
        <StyledLine className={containerClassName}>
            {labelElement}
            <StyledTypographyUrls className={typographyClassName} variant={"body2Highlight"}>
                {items.map(({ label, url }, index) => (
                    <Fragment key={index}>
                        {index > 0 && ", "}
                        {url ? (
                            <Link href={url} size={"small"} onClick={onLinkClick}>
                                {label}
                            </Link>
                        ) : (
                            label
                        )}
                    </Fragment>
                ))}
            </StyledTypographyUrls>
        </StyledLine>
    );
};

export default CustomMetadataLine;

const StyledLine = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    "& + &": {
        marginTop: theme.spacing(1),
    },
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
}));

// Text metadata may contain rich markup with divs and paragraphs, but they still should be displayed inline
const StyledTypographyWithHtml = styled(Typography)({
    "div, p": {
        display: "inline",
    },
});

const StyledTypographyUrls = styled(Typography)({
    wordBreak: "break-word",
});
